import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { logAnalyticsEvent } from 'helpers/loggers';
import { ANALYTICS_EVENT_NAME } from 'helpers/constants';
import useIsMobile from 'hooks/useIsMobile';

import {
  getAbortSignal,
  getApiHost,
  getMutationFetchOptions,
  parseHttpResponse,
} from '../utils';
import { MenuCategory, Modifier, ModifierCategory } from '../menu';

export const ITEM_RECOMMENDATIONS_QUERY_KEY = 'itemRecommendations';

const ITEM_RECOMMENDATIONS_API_VERSION = '5.0';

type ItemRecommendationsQueryParams = {
  restaurantId: string;
  menuId: string;
  selectedItemIds: string[];
  requestedTime?: string;
  enabled: boolean;
};

export const useItemRecommendationsQuery: (
  params: ItemRecommendationsQueryParams
) => UseQueryResult<ItemRecommendations, Error> = ({
  restaurantId,
  menuId,
  selectedItemIds,
  requestedTime,
  enabled,
}) => {
  const isMobile = useIsMobile();
  return useQuery<ItemRecommendations>({
    enabled,
    queryKey: [
      ITEM_RECOMMENDATIONS_QUERY_KEY,
      restaurantId,
      menuId,
      requestedTime,
    ],
    queryFn: async ({ signal }) => {
      const apiHost = getApiHost();
      const url = new URL(
        `/api/restaurant/${restaurantId}/item-recommendations`,
        apiHost
      );

      let response: Response;
      try {
        response = await fetch(url, {
          ...getMutationFetchOptions(
            'POST',
            {
              menu_id: menuId,
              selected_item_ids: selectedItemIds,
              ...(requestedTime ? { requested_time: requestedTime } : {}),
            },
            ITEM_RECOMMENDATIONS_API_VERSION
          ),
          ...getAbortSignal(signal, 4000),
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'TimeoutError') {
          logAnalyticsEvent({
            eventName: ANALYTICS_EVENT_NAME.upsellRecommendationTimeoutError,
            attributes: {
              restaurant_id: restaurantId,
              viewport: isMobile ? 'mobile' : 'desktop',
            },
          });
        }
        throw error;
      }

      return parseHttpResponse(response);
    },
    refetchOnMount: 'always',
  });
};

export type ItemRecommendations = {
  id: string;
  menu_categories: MenuCategory[];
  modifier_categories: ModifierCategory[];
  modifiers: Modifier[];
};
